import axios from "./axios";

// helpers
import { pathToUrl } from "./query/reactQuery/pathToUrl";
import API_ROUTES from "./routes.constant";

// interfaces
import { ICampaign } from "@/types/api/Campaign";
import { IAbstractEventFormValuesCampaignDetails } from "@/components/AbstractEventForm/IAbstractEventForm";

const BASE = "/campaigns";

export function addSpaceToCampaign(campaignId: number, spaceId: number) {
  return axios.post(`${BASE}/${campaignId}/items`, { space: spaceId });
}

export async function createActivities(campaignId: number, activityId: number, data: unknown) {
  return axios.patch(`${BASE}/${campaignId}/items/${activityId}`, data);
}

export function getReleaseCampaignOptions(campaignId: number) {
  const fetchKey = campaignId
    ? pathToUrl(API_ROUTES.campaign.singleCampaignReleaseAllOptions, { id: campaignId })
    : "";

  return axios.get(fetchKey)
}

export async function editCampaign(id: number, data: unknown) {
  return axios.put(`${BASE}/${id}`, data);
}

export function addSpaceToActivity(campaignId: number, spaceId: number, data: unknown) {
  return axios.post(`${BASE}/${campaignId}/items/${spaceId}/periods`, data);
}

export function createCampaign(data: unknown) {
  return axios.post<ICampaign>(`${BASE}`, data);
}

export async function editCampaignPeriodData(campaignId: number, periodId: number, data: unknown) {
  return axios.put(`/campaigns/${campaignId}/periods/${periodId}`, data);
}

export async function createInterest(periodId: number, campaignId: number, data: unknown = {}) {
  return axios.put(`/campaigns/${campaignId}/periods/${periodId}/interest`, data);
}

export async function addSpaceToSchedule(
  campaignId: number,
  scheduleId: number | string,
  campaignSpaces: number[],
  periods: number[]
) {
  const data: { spaces?: number[], periods?: number[] } = {};

  if (campaignSpaces && campaignSpaces.length > 0) {
    data.spaces = campaignSpaces;
  }
  if (periods && periods.length > 0) {
    data.periods = periods;
  }

  return axios.put(`${BASE}/${campaignId}/schedules/${scheduleId}/items`, data);
}

export async function getSingleCampaign(id: number) {
  const fetchKey = id
    ? pathToUrl(API_ROUTES.campaign.singleCampaign, { id })
    : null;

  const res = await axios.get(fetchKey as string)

  return res.data as ICampaign;
}

export function getSpaceInstallPack(id: number | string, spaceId: number) {
  const fetchKey = (id && spaceId)
    ? pathToUrl(API_ROUTES.campaign.downloadInstallPack, { id, spaceId })
    : null;
  
  return axios.get(fetchKey as string, { responseType: "blob" });
}

export async function getCampaignBookingForm(id?: number | string) {
  const fetchKey = id
    ? pathToUrl(API_ROUTES.campaign.singleCampaignBookingForm, { id })
    : null;

  const res = await axios.get(fetchKey as string)

  return res.data as IAbstractEventFormValuesCampaignDetails;
}

export async function postCampaignArchive({ id, token }: { id: number | string, token: string }) {
  const fetchKey = id
    ? pathToUrl(API_ROUTES.campaign.singleCampaignArchive, { id })
    : null;

  return axios.post(`${fetchKey}?token=${token}`);
}

export async function postCampaignExtendArchivalDate({ id, token }: { id: number | string, token: string }) {
  const fetchKey = id
    ? pathToUrl(API_ROUTES.campaign.singleCampaignExtendArchivalDate, { id })
    : null;

  return axios.post(`${fetchKey}?token=${token}`);
}

export async function postCampaignRequestArchivalDateExtension({ id, token }: { id: number | string, token?: string }) {
  const fetchKey = id
    ? pathToUrl(API_ROUTES.campaign.singleCampaignRequestArchivalDateExtension, { id })
    : null;

  return axios.post(`${fetchKey}${token ? `?token=${token}` : ""}`);
}

export async function postCampaignUnarchive({ id, token }: { id: number | string, token: string }) {
  const fetchKey = id
    ? pathToUrl(API_ROUTES.campaign.singleCampaignUnarchive, { id })
    : null;

  return axios.post(`${fetchKey}?token=${token}`);
}

export async function postCampaignBulkAddSpaces({ id, data }: { id: number | string, data: { spaces: number[] } }) {
  const fetchKey = id
    ? pathToUrl(API_ROUTES.campaign.singleCampaignBulkAddSpaces, { id })
    : null;

  return axios.post(fetchKey as string, data);
}

export async function postCampaignRequestUnarchival({ id }: { id: number | string }) {
  const fetchKey = id
    ? pathToUrl(API_ROUTES.campaign.singleCampaignRequestUnarchival, { id })
    : null;

  return axios.post(fetchKey as string);
}

